import React, { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import {
    IMaintenancePackageForm,
    TMaintenancePackageFormikValues,
} from './types';
import {
    Card,
    CardHeader,
    CardContent,
    Divider,
    TextField,
    Button,
    IconButton,
    Grid,
    FormControl,
    Select,
    MenuItem,
    Typography,
    Box,
} from '@material-ui/core';
import { Add, Delete } from '@material-ui/icons';
import { Formik, Form, FieldArray } from 'formik';
import useStore from 'src/hooks/useStore';
import { IStore } from 'src/interfaces/store';
import { makeStyles } from '@material-ui/core/styles';
import { Autocomplete } from '@material-ui/lab';
import { DEFAULT_SERVICE_DETAILS } from 'src/constants/maintenancePackage';
import { Plus } from 'react-feather';

const defaultValues: TMaintenancePackageFormikValues = {
    name: '',
    details: [{ title: '', description: '' }],
    stores: [''],
};

const useStyles = makeStyles((theme) => ({
    formContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2),
    },
    addDetailButton: {
        maxWidth: 250,
        selfAlign: 'center',
    },
    createButton: {
        minWidth: 150,
        height: 56,
    },
}));

export const MaintenancePackageForm: React.FC<IMaintenancePackageForm> = ({
    mode,
    initialValues = defaultValues,
    onSubmit,
}) => {
    const { t } = useTranslation();
    const { stores } = useStore();
    const classes = useStyles();

    return (
        <Formik
            initialValues={initialValues}
            enableReinitialize
            onSubmit={onSubmit}
        >
            {({ values, handleChange }) => (
                <Form>
                    <Card>
                        <CardHeader
                            title={
                                mode === 'create'
                                    ? t('maintenancePackage.Create')
                                    : t('maintenancePackage.Update')
                            }
                        />
                        <Divider />
                        <CardContent className={classes.formContainer}>
                            <TextField
                                name="name"
                                label={t('maintenancePackage.Name')}
                                value={values.name}
                                onChange={handleChange}
                                fullWidth
                                required
                                variant="outlined"
                                InputLabelProps={{
                                    shrink: true,
                                }}
                            />

                            <FieldArray name="details">
                                {({ push, remove }) => (
                                    <Fragment>
                                        <Typography variant="h5">
                                            {t('maintenancePackage.Details')}
                                        </Typography>

                                        {values.details.map((detail, index) => (
                                            <Grid
                                                container
                                                spacing={2}
                                                alignItems="center"
                                                key={index}
                                            >
                                                <Grid item xs={5}>
                                                    <Autocomplete
                                                        freeSolo
                                                        options={DEFAULT_SERVICE_DETAILS.map(
                                                            (service) =>
                                                                t(service)
                                                        )}
                                                        value={detail.title}
                                                        onChange={(
                                                            _,
                                                            newValue
                                                        ) => {
                                                            handleChange({
                                                                target: {
                                                                    name: `details.${index}.title`,
                                                                    value:
                                                                        newValue ||
                                                                        '',
                                                                },
                                                            });
                                                        }}
                                                        onInputChange={(
                                                            _,
                                                            newInputValue
                                                        ) => {
                                                            handleChange({
                                                                target: {
                                                                    name: `details.${index}.title`,
                                                                    value: newInputValue,
                                                                },
                                                            });
                                                        }}
                                                        renderInput={(
                                                            params
                                                        ) => (
                                                            <TextField
                                                                {...params}
                                                                label={t(
                                                                    'maintenancePackage.DetailsTitle'
                                                                )}
                                                                required
                                                                variant="outlined"
                                                            />
                                                        )}
                                                    />
                                                </Grid>

                                                <Grid item xs={6}>
                                                    <TextField
                                                        name={`details.${index}.description`}
                                                        label={t(
                                                            'maintenancePackage.DetailsDescription'
                                                        )}
                                                        value={
                                                            detail.description
                                                        }
                                                        onChange={handleChange}
                                                        fullWidth
                                                        required
                                                        variant="outlined"
                                                    />
                                                </Grid>

                                                <Grid item xs={1}>
                                                    <IconButton
                                                        onClick={() =>
                                                            remove(index)
                                                        }
                                                        disabled={
                                                            values.details
                                                                .length === 1
                                                        }
                                                    >
                                                        <Delete />
                                                    </IconButton>
                                                </Grid>
                                            </Grid>
                                        ))}
                                        <Box
                                            display="flex"
                                            justifyContent="center"
                                        >
                                            <Button
                                                startIcon={<Add />}
                                                className={
                                                    classes.addDetailButton
                                                }
                                                onClick={() =>
                                                    push({
                                                        title: '',
                                                        description: '',
                                                    })
                                                }
                                            >
                                                {t(
                                                    'maintenancePackage.AddDetail'
                                                )}
                                            </Button>
                                        </Box>
                                    </Fragment>
                                )}
                            </FieldArray>

                            <FieldArray name="stores">
                                {({ push, remove }) => (
                                    <Fragment>
                                        <Typography variant="h5">
                                            {t('maintenancePackage.Stores')}
                                        </Typography>

                                        {values.stores.map((storeId, index) => (
                                            <Grid
                                                container
                                                spacing={2}
                                                alignItems="center"
                                                key={index}
                                            >
                                                <Grid item xs={11}>
                                                    <FormControl
                                                        fullWidth
                                                        required
                                                    >
                                                        <Select
                                                            value={storeId}
                                                            onChange={(e) =>
                                                                handleChange({
                                                                    target: {
                                                                        name: `stores.${index}`,
                                                                        value: e
                                                                            .target
                                                                            .value,
                                                                    },
                                                                })
                                                            }
                                                            variant="outlined"
                                                        >
                                                            {stores.map(
                                                                (
                                                                    store: IStore
                                                                ) => (
                                                                    <MenuItem
                                                                        key={
                                                                            store._id
                                                                        }
                                                                        value={
                                                                            store._id
                                                                        }
                                                                    >
                                                                        {
                                                                            store.name
                                                                        }
                                                                    </MenuItem>
                                                                )
                                                            )}
                                                        </Select>
                                                    </FormControl>
                                                </Grid>

                                                <Grid item xs={1}>
                                                    <IconButton
                                                        onClick={() =>
                                                            remove(index)
                                                        }
                                                        disabled={
                                                            values.stores
                                                                .length === 1
                                                        }
                                                    >
                                                        <Delete />
                                                    </IconButton>
                                                </Grid>
                                            </Grid>
                                        ))}
                                        <Box
                                            display="flex"
                                            justifyContent="center"
                                        >
                                            <Button
                                                startIcon={<Add />}
                                                onClick={() => push('')}
                                                className={
                                                    classes.addDetailButton
                                                }
                                            >
                                                {t(
                                                    'maintenancePackage.AddStore'
                                                )}
                                            </Button>
                                        </Box>
                                    </Fragment>
                                )}
                            </FieldArray>

                            <Box display="flex" justifyContent="flex-end">
                                <Button
                                    type="submit"
                                    color="primary"
                                    variant="contained"
                                    startIcon={<Plus />}
                                    className={classes.createButton}
                                >
                                    {mode === 'create'
                                        ? t('Buttons.Create')
                                        : t('Buttons.Update')}
                                </Button>
                            </Box>
                        </CardContent>
                    </Card>
                </Form>
            )}
        </Formik>
    );
};
